import * as React from 'react';
import * as _ from 'underscore';
import * as $ from 'jquery';

import { IGlob } from '../../../../pdc-platform/src/ts/Glob';
import { AlignmentType, ATTRIBUTE_TYPES } from '../../../../_common/platform/ts/ConfigurationConstants';
import * as DateTimeFunctions from '../../../../_common/platform/ts/DateTimeFunctions';
import { IValuesCollection } from '../../ts/appTheme';
import ValueChooser from './ValueChooser';
import StyleChooser from './StyleChooser';
import TemplateEditor from './TemplateEditor';
import { getAttributeTypeByAggregate, IValueFormat } from '../../ts/AttrQueryValuesFormat';
import { MappingType } from '../../ts/Interfaces';
import { IAttributeName } from '../filtering/Interfaces';
import { isDecimalVisible } from './FormatChooserValueSelector';
import { IAttributeTypeGetter, IConfirmValue, IDefaultFormatGetter, IFormatChooserValue } from './Interfaces';
import { IMappingValue } from '../IntervalsSelect';

enum TABS {
    VALUE,
    STYLE,
    TEMPLATE
}

export interface IFormatChooserPopupOptions {
    editorConfig?;
    value?: IFormatChooserValue;
    attributeType: string;
    theme: IValuesCollection;
    isScalar?: boolean;
    dataSourceID: number;
    showAlignmentSelector?: boolean;
    popupTitle: string;
    customizeFormat?: boolean;
    isExpression?: boolean;
    showStyle?: boolean;
    showTemplate?: boolean;
    showRawTemplate?: boolean;
    activeTab?: TABS;
    offset: {
        left: number;
        top: number;
    };
    glob: IGlob;

    getDefaultAttributeType: IAttributeTypeGetter;
    getDefaultFormat: IDefaultFormatGetter;

    onChange(obj: IFormatChooserValue): void;
}

export function getDefaultAttributeType(
    value: IAttributeName,
    glob: IGlob,
    dataSourceID: number,
    getDefaultAttributeType: IAttributeTypeGetter
) {
    if (getDefaultAttributeType != null) {
        return getDefaultAttributeType(value);
    }

    return glob.EntityModelsStore.getDefaultAttributeType(value, dataSourceID);
}

function getTypeCalculated(
    value: IFormatChooserValue,
    dataSourceID: number,
    attributeType: string,
    isScalar: boolean,
    glob: IGlob,
    getAttributeType: IAttributeTypeGetter
): string {
    let result = attributeType || (value as any).attrType || (value as any).type;

    if (result == null) {
        result = getDefaultAttributeType(value, glob, dataSourceID, getAttributeType);
    }

    if (result == null || result === ATTRIBUTE_TYPES.ENUMERATION || result === '') {
        result = 'count';
    }

    result = getAttributeTypeByAggregate(value, result);

    if (isScalar) {
        if (
            !DateTimeFunctions.isTypeOfInteger(result) &&
            result !== ATTRIBUTE_TYPES.REAL &&
            result !== ATTRIBUTE_TYPES.BOOLEAN &&
            result !== ATTRIBUTE_TYPES.ANYTYPE &&
            !DateTimeFunctions.isTypeOfAnyDate(result)
        ) {
            return null;
        }
    } else if (!DateTimeFunctions.isTypeOfAnyDate(result)) {
        return null;
    }

    return result;
}

interface IFormatChooserPopupState extends IValueFormat {
    useDiscreteMapping?: boolean;
    backgroundColor?: string;
    foregroundColor?: string;
    fontFace?: string;
    fontSize?: string;
    fontWeight?: string;
    fontItalic?: boolean;
    alignment?: 'center' | 'left' | 'right';

    backgroundColorSchema?: string; //LL: 13.11.20: left for compatibility
    foregroundColorSchema?: string; //LL: 13.11.20: left for compatibility
    background_mappingName?: string;
    background_mappingType?: MappingType;
    foreground_mappingName?: string;
    foreground_mappingType?: MappingType;

    template?: string;
    height?: number;
    width?: number;
}

function createState(options: IFormatChooserPopupOptions) {
    let backColor: string = null;
    let foreColor: string = null;
    let alignment: AlignmentType = null;
    let fontFace: string = null;
    let fontSize: string = null;
    let fontWeight: string = null;
    let fontItalic: boolean = null;

    if (options.value.cellStyle != null) {
        backColor = options.value.cellStyle.backgroundColor;
        foreColor = options.value.cellStyle.foregroundColor;
        alignment = options.value.cellStyle.alignment;
        fontFace = options.value.cellStyle.fontFace;
        fontSize = options.value.cellStyle.fontSize;
        fontWeight = options.value.cellStyle.fontWeight;
        fontItalic = options.value.cellStyle.fontItalic;
    }

    let precisionNew = 0; //VR: calculate PRECISION before select the Format (see here f. handleFormatSelected())

    const valueType = getTypeCalculated(
        options.value,
        options.dataSourceID,
        options.attributeType,
        options.isScalar,
        options.glob,
        options.getDefaultAttributeType
    );

    if (isDecimalVisible(valueType, options.value.formatFront || 'default')) {
        precisionNew = 2;

        if (options.value.precision != null) {
            precisionNew = options.value.precision;
        }
    } else {
        precisionNew = 0;
    }

    const result: IFormatChooserPopupState = {
        alignment: alignment,
        formatFront: options.value.formatFront || 'default',
        customFormat: options.value.customFormat || '',
        precision: precisionNew,
        useDigitGrouping: options.value.useDigitGrouping,
        overwriteInheritedUseDigitGrouping: options.value.overwriteInheritedUseDigitGrouping,
        formatZeroAsBlank: options.value.formatZeroAsBlank,
        overwriteInheritedFormatZeroAsBlank: options.value.overwriteInheritedFormatZeroAsBlank,
        useFiscalPrefix: options.value.useFiscalPrefix,
        fiscalYearDivider: options.value.fiscalYearDivider,
        formatAsType: options.value.formatAsType,
        currency: options.value.currency,
        backgroundColor: backColor,
        foregroundColor: foreColor,
        fontFace: fontFace,
        fontSize: fontSize,
        fontWeight: fontWeight,
        fontItalic: fontItalic,

        //LL: 13.11.20: left for compatibility
        backgroundColorSchema: options.value.cellStyle?.backgroundColorSchema,
        foregroundColorSchema: options.value.cellStyle?.foregroundColorSchema,
        background_mappingName: options.value.cellStyle?.background_mappingName,
        background_mappingType: options.value.cellStyle?.background_mappingType,
        foreground_mappingName: options.value.cellStyle?.foreground_mappingName,
        foreground_mappingType: options.value.cellStyle?.foreground_mappingType,

        template: options.value.template,
        height: 370,
        width: 540
    };

    return result;
}

export function FormatChooserPopup(props: {
    setConfirmValueGetter?(getConfirmValue: () => IConfirmValue);
    unsetConfirmValueGetter?();
    zIndex: number;
    options: IFormatChooserPopupOptions;
    close(): void;
}) {
    const [state, setState] = React.useState(() => createState(props.options));
    const [activeTab, setActiveTab] = React.useState<TABS>(
        props.options.activeTab || (props.options.customizeFormat ? TABS.VALUE : TABS.STYLE)
    );

    React.useEffect(() => {
        const getConfirmValue: () => IConfirmValue = () => {
            return {
                precision: state.precision,
                formatFront: state.formatFront,
                currency: state.currency,
                customFormat: state.customFormat,
                useDigitGrouping: state.useDigitGrouping,
                overwriteInheritedUseDigitGrouping: state.overwriteInheritedUseDigitGrouping,
                formatZeroAsBlank: state.formatZeroAsBlank,
                overwriteInheritedFormatZeroAsBlank: state.overwriteInheritedFormatZeroAsBlank,
                useFiscalPrefix: state.useFiscalPrefix,
                fiscalYearDivider: state.fiscalYearDivider,
                formatAsType: state.formatAsType,

                cellStyle: {
                    backgroundColor: state.backgroundColor,
                    foregroundColor: state.foregroundColor,

                    backgroundColorSchema: state.backgroundColorSchema, //LL: 13.11.20: left for compatibility
                    foregroundColorSchema: state.foregroundColorSchema, //LL: 13.11.20: left for compatibility
                    background_mappingName: state.background_mappingName,
                    background_mappingType: state.background_mappingType,
                    foreground_mappingName: state.foreground_mappingName,
                    foreground_mappingType: state.foreground_mappingType,

                    alignment: state.alignment,
                    fontFace: state.fontFace,
                    fontSize: state.fontSize,
                    fontWeight: state.fontWeight,
                    fontItalic: state.fontItalic
                },
                template: state.template
            };
        };

        props.setConfirmValueGetter(getConfirmValue);

        return () => {
            if (typeof props.unsetConfirmValueGetter === 'function') {
                props.unsetConfirmValueGetter();
            }
        };
    }, [state, props.setConfirmValueGetter, props.unsetConfirmValueGetter, props.close]);

    const handleOnTemplateChange = (value: string) => {
        setState({ ...state, template: value });
    };

    const handleOnBackgroundColorChange_ColorBox = (value: string) => {
        setState({ ...state, backgroundColor: value });
    };

    const handleOnForegroundColorChange_ColorBox = (value: string) => {
        setState({ ...state, foregroundColor: value });
    };

    const handleOnChangeFontFace = (value: string) => {
        setState({ ...state, fontFace: value });
    };

    const handleOnChangeFontSize = (value: string) => {
        setState({ ...state, fontSize: value });
    };

    const handleOnChangeFontWeight = (value: string) => {
        setState({ ...state, fontWeight: value });
    };

    const handleOnChangeFontItalic = (value: boolean) => {
        setState({ ...state, fontItalic: value });
    };

    const handleOnAlignmentChange = (value: string) => {
        setState({ ...state, alignment: value as AlignmentType });
    };

    const handleOnBackColorMappingChange = (value: IMappingValue) => {
        setState({
            ...state,
            backgroundColorSchema: value.mappingName, //LL: for compatibility with old functionality
            background_mappingName: value.mappingName,
            background_mappingType: value.mappingType
        });
    };

    const handleOnForeColorMappingChange = (value: IMappingValue) => {
        setState({
            ...state,
            foregroundColorSchema: value.mappingName, //LL: for compatibility with old functionality
            foreground_mappingName: value.mappingName,
            foreground_mappingType: value.mappingType
        });
    };

    const handleOnValueChooserChange = (value: IValueFormat) => {
        if (value != null) {
            setState({ ...state, ...value });
        } else {
            setState({
                ...state,
                precision: null,
                formatFront: null,
                currency: null,
                customFormat: null,
                useDigitGrouping: null,
                overwriteInheritedUseDigitGrouping: false,
                formatZeroAsBlank: null,
                overwriteInheritedFormatZeroAsBlank: false,
                useFiscalPrefix: null,
                fiscalYearDivider: null,
                formatAsType: null
            });
        }
    };

    const handleOnActivateTab = React.useCallback((tab: TABS) => {
        setActiveTab(tab);
    }, []);

    const { options } = props;

    let tabContent: React.JSX.Element;

    switch (activeTab) {
        case TABS.STYLE:
            tabContent = (
                <StyleChooser
                    options={options}
                    zIndex={props.zIndex}
                    glob={options.glob}
                    backgroundColor={state.backgroundColor}
                    foregroundColor={state.foregroundColor}
                    fontFace={state.fontFace}
                    fontSize={state.fontSize}
                    fontWeight={state.fontWeight}
                    fontItalic={state.fontItalic}
                    backgroundColorSchema={state.backgroundColorSchema}
                    background_mappingType={state.background_mappingType}
                    foregroundColorSchema={state.foregroundColorSchema}
                    foreground_mappingType={state.foreground_mappingType}
                    alignment={state.alignment}
                    onBackgroundColorChange_ColorBox={handleOnBackgroundColorChange_ColorBox}
                    onForegroundColorChange_ColorBox={handleOnForegroundColorChange_ColorBox}
                    onChangeFontFace={handleOnChangeFontFace}
                    onChangeFontSize={handleOnChangeFontSize}
                    onChangeFontWeight={handleOnChangeFontWeight}
                    onChangeFontItalic={handleOnChangeFontItalic}
                    onAlignmentChange={handleOnAlignmentChange}
                    onBackColorMappingChange={handleOnBackColorMappingChange}
                    onForeColorMappingChange={handleOnForeColorMappingChange}
                />
            );
            break;

        case TABS.VALUE:
            const attributeType = getTypeCalculated(
                options.value,
                options.dataSourceID,
                options.attributeType,
                options.isScalar,
                options.glob,
                options.getDefaultAttributeType
            );

            tabContent = (
                <ValueChooser
                    value={{
                        precision: state.precision,
                        formatFront: state.formatFront,
                        currency: state.currency,
                        customFormat: state.customFormat,
                        useDigitGrouping: state.useDigitGrouping,
                        overwriteInheritedUseDigitGrouping: state.overwriteInheritedUseDigitGrouping,
                        formatZeroAsBlank: state.formatZeroAsBlank,
                        overwriteInheritedFormatZeroAsBlank: state.overwriteInheritedFormatZeroAsBlank,
                        useFiscalPrefix: state.useFiscalPrefix,
                        fiscalYearDivider: state.fiscalYearDivider,
                        formatAsType: state.formatAsType
                    }}
                    formatChooserValue={options.value}
                    attributeType={attributeType}
                    isScalar={options.isScalar}
                    theme={options.theme}
                    getDefaultFormat={options.getDefaultFormat}
                    onChange={handleOnValueChooserChange}
                />
            );
            break;

        case TABS.TEMPLATE:
            tabContent = (
                <TemplateEditor
                    editorConfig={options.editorConfig}
                    template={state.template}
                    formatChooserValue={options.value}
                    glob={options.glob}
                    onTemplateChange={handleOnTemplateChange}
                />
            );
            break;
    }

    return (
        <div className="format-chooser">
            <ul className="nav nav-tabs">
                {options.customizeFormat && (
                    <Tab caption={'Value'} tabID={TABS.VALUE} activeTab={activeTab} onActivate={handleOnActivateTab} />
                )}
                {options.showStyle && (
                    <Tab caption={'Style'} tabID={TABS.STYLE} activeTab={activeTab} onActivate={handleOnActivateTab} />
                )}
                {options.showTemplate && (
                    <Tab
                        caption={'Template'}
                        tabID={TABS.TEMPLATE}
                        activeTab={activeTab}
                        onActivate={handleOnActivateTab}
                    />
                )}
            </ul>
            <div className="tab-content">
                <div className="tab-pane active">{tabContent}</div>
            </div>
        </div>
    );
}

function Tab(props: { caption: string; tabID: TABS; activeTab: TABS; onActivate(tabID: TABS): void }) {
    return (
        <li className="nav-item">
            <a
                href="#"
                className={'nav-link' + (props.tabID === props.activeTab ? ' active' : '')}
                onClick={(e) => {
                    e.preventDefault();
                    props.onActivate(props.tabID);
                }}
            >
                {props.caption}
            </a>
        </li>
    );
}
